import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { fetchEmployees } from '../../../slices/employees/employeesSlice';
import { fetchSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { fetchCustomers } from '../../../slices/customers/customersSlice';
import {
  clearDocuments,
  fetchDocuments,
  selectAllDocuments,
} from '../../../slices/documents/documentsSlice';
import { selectUser } from '../../../slices/user/userSlice';
import { DOCUMENT_CATEGORIES } from '../types';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import {
  fetchBusinessProfileById,
  fetchBusinessProfileContactById,
} from '../../../slices/business-profile/businessProfileSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helpers } from '../../../common/helpers/helpers';

const useDocumentsKeyDates = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];
  const documentsCount = useAppSelector((state) => state.documents.documentsCount);
  const [currentPage, setCurrentPage] = useState(1);
  const documentItems = useAppSelector(selectAllDocuments);
  const documentContractsLoading = useAppSelector((state) => state.documentContracts.status);
  const documentUploadsLoading = useAppSelector((state) => state.documents.status);
  const employeesLoading = useAppSelector((state) => state.employees.status);
  const customersLoading = useAppSelector((state) => state.customers.status);
  const suppliersLoading = useAppSelector((state) => state.suppliers.status);
  const businessProfileLoading = useAppSelector((state) => state.businessProfile.status);
  const currentUser = useAppSelector(selectUser);
  const [params] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>('Documents');
  const activeTabInitialValue: string = params.get('activeTab')
    ? Helpers.capitalizeFirstLetter(params.get('activeTab'))
    : null;
  const navigate = useNavigate();
  const requestData = useMemo(
    () => ({
      data: {
        user: currentUser.id,
        sort: 'key_date_end_date',
        order: 'ASC',
        hasKeyDate: true,
        type: 'nonSubscription',
        page: currentPage,
      },
    }),
    [currentUser.id, currentPage]
  );

  const getNextPage = () => {
    setCurrentPage(currentPage + 1);
  };

  const getRequestedPage = (passedPage: number) => {
    setCurrentPage(passedPage);
  };

  const getPrevPage = () => {
    setCurrentPage(currentPage - 1);
  };

  const documents = [...documentItems];

  const handleTabChange = (tab: DOCUMENT_CATEGORIES) => {
    setCurrentPage(1);
    setActiveTab(tab);
  };

  const handleFetchDocuments = useCallback(() => {
    dispatch(clearDocuments());

    if (activeTab === 'Subscriptions' || activeTabInitialValue === 'Subscriptions') {
      requestData.data.type = 'Subscription';
      dispatch(fetchDocuments(requestData));
    } else {
      requestData.data.type = 'nonSubscription';
      dispatch(fetchDocuments(requestData));
    }
  }, [activeTab, activeTabInitialValue, dispatch, requestData]);

  const getEmployees = useCallback(() => {
    if (company) {
      if (employeesLoading === 'idle') {
        dispatch(fetchEmployees({ company_id: company.id }));
      }
    }
  }, [employeesLoading, company, dispatch]);

  const getCustomers = useCallback(() => {
    if (company) {
      if (customersLoading === 'idle') {
        dispatch(fetchCustomers({ company_id: company.id, page_size: 100 }));
      }
    }
  }, [customersLoading, company, dispatch]);

  const getSuppliers = useCallback(() => {
    if (company) {
      if (suppliersLoading === 'idle') {
        dispatch(fetchSuppliers({ company_id: company.id, page_size: 100 }));
      }
    }
  }, [suppliersLoading, company, dispatch]);

  const getBusinessContacts = useCallback(() => {
    if (company) {
      if (businessProfileLoading === 'idle') {
        dispatch(fetchBusinessProfileContactById({ id: company.id }));
      }
    }
  }, [businessProfileLoading, company, dispatch]);

  const getBusinessDetails = useCallback(() => {
    if (company) {
      if (businessProfileLoading === 'idle') {
        dispatch(fetchBusinessProfileById({ id: company.id }));
      }
    }
  }, [businessProfileLoading, company, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearDocuments());
    };
  }, [dispatch]);

  useEffect(() => {
    getEmployees();
  }, [getEmployees]);

  useEffect(() => {
    getSuppliers();
  }, [getSuppliers]);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  useEffect(() => {
    getBusinessContacts();
  }, [getBusinessContacts]);

  useEffect(() => {
    getBusinessDetails();
  }, [getBusinessDetails]);

  useEffect(() => {
    handleFetchDocuments();
  }, [handleFetchDocuments]);

  /*useEffect(() => {
    dispatch(fetchDocuments(requestData));
  }, [currentPage]);*/

  useEffect(() => {
    if (activeTabInitialValue) {
      setActiveTab(activeTabInitialValue);
      navigate(window.location.pathname);
    }
  }, [activeTabInitialValue, navigate]);

  return {
    documents,
    documentContractsLoading,
    documentUploadsLoading,
    employeesLoading,
    suppliersLoading,
    customersLoading,
    businessProfileLoading,
    activeTab,
    documentsCount,
    getNextPage,
    getPrevPage,
    getRequestedPage,
    handleTabChange,
    currentPage,
    setCurrentPage,
  };
};

export { useDocumentsKeyDates };
